import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as userApi from '../Api/userApi';

const USER_KEY = 'users';

export const useUserDetails = () => {
	const token = localStorage.getItem('token');
	return useQuery({
		queryKey: [USER_KEY, 'me'],
		queryFn: () => userApi.getUserDetails(),
		enabled: !!token && token.trim() !== '', // Only fetch if token exists and is not empty
		retry: (failureCount, error) => {
			const status = error?.response?.status;
			// Don't retry when the session is invalid or the user no longer exists
			if (status === 401 || status === 404) {
				return false;
			}
			return failureCount < 3;
		},
	});
};

export const useUser = (id) =>
	useQuery({
		queryKey: [USER_KEY, id],
		queryFn: () => userApi.getUserById(id),
		enabled: !!id,
	});

export const useUsersByCompany = (companyId, options = {}) =>
	useQuery({
		queryKey: [USER_KEY, 'company', companyId],
		queryFn: async () => {
			const response = await userApi.getUsersByCompany(companyId);
			const data = response.data || response;
			// Ensure we always return an array
			return Array.isArray(data) ? data : [];
		},
		enabled: !!companyId,
		...options,
	});

export const useUsersByRole = (role) =>
	useQuery({
		queryKey: [USER_KEY, 'role', role],
		queryFn: () => userApi.getUsersByRole(role),
		enabled: !!role,
	});

export const useAllUsers = (options = {}) =>
	useQuery({
		queryKey: [USER_KEY],
		queryFn: () => userApi.getAllUsers(),
		...options,
	});

export const useCreateUser = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: userApi.createUser,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [USER_KEY] });
		},
	});
};

export const useUpdateUser = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }) => userApi.updateUserDetails(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [USER_KEY] });
		},
	});
};

export const useDeleteUser = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id) => userApi.deleteUser(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [USER_KEY] });
		},
	});
};

export const useUpdateUserPermissions = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, permissions, changedUniqueKey }) =>
			userApi.updateUserPermissions(id, { permissions, changedUniqueKey }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [USER_KEY] });
		},
	});
};

export const usePromoteToCompanyAdmin = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id) => userApi.promoteToCompanyAdmin(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [USER_KEY] });
		},
	});
};

export const useDemoteCompanyAdmin = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id) => userApi.demoteCompanyAdmin(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [USER_KEY] });
		},
	});
};

export const useSetUserSuspension = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, isSuspended }) => userApi.setUserSuspension(id, isSuspended),
		onSuccess: (_data, { id }) => {
			queryClient.invalidateQueries({ queryKey: [USER_KEY] });
			queryClient.invalidateQueries({ queryKey: [USER_KEY, id] });
			queryClient.invalidateQueries({ queryKey: ['employees'] });
			queryClient.invalidateQueries({ queryKey: ['employees', id] });
		},
	});
};
